import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, DataTable, EmptyState, Pane, PaneHead, Segmented, StatCard, type TableRow,
} from '@/components/kit';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePayrollRun } from '@/pages/roles/accountant/lib/useAccounts';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { DayBars } from '../components/Charts';
import {
  doctorName, lastCompleteMonth, monthLabel, monthName, monthRange, num, shiftMonth,
  monthChip, money,
} from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { DealEditor } from '../components/DealEditor';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import {
  useAppointments, useBranches, useInvoices, useProviders, useUnsignedNotes,
} from '../lib/useManagerData';

/**
 * Clinic manager · Visits & doctors — `g_visits.dc.html`.
 *
 * PER-DOCTOR RETURN AND NO-SHOW, AGGREGATE ONLY. The board's closing line is
 * the rule: no visit lists, no names, no diagnoses — the wall holds inside the
 * reports too. Every figure here is a count over appointments; not one of them
 * reads a patient.
 *
 * RETURN is the share of a doctor's visits that were follow-ups — the patients
 * who came back. NO-SHOW is the share of everything booked that never arrived,
 * which is a different denominator on purpose: a no-show is not a visit.
 */
type LatencyPeriod = 'today' | 'week' | 'month';
interface DoctorLatency { doctorId: string; avgDays: number | null; reviewed: number }

/** Per doctor: mean days from a result landing to the doctor reviewing it. */
function useResultsLatency(period: LatencyPeriod) {
  const q = useQuery({
    queryKey: ['cm-results-latency', period],
    staleTime: 120_000,
    retry: false,
    queryFn: async (): Promise<DoctorLatency[]> => {
      const res = await apiClient.get('/dashboard/results-latency', { params: { period } });
      return (res.data?.data ?? res.data ?? []) as DoctorLatency[];
    },
  });
  return q.data ?? [];
}

interface FlowTiming {
  flow: string;
  runs: number;
  steps: Array<{ label: string; avgMin: number; runs: number }>;
  bottleneck: string | null;
}

/** Average minutes per flow step — each step records its own start and finish. */
function useFlowTimings() {
  const q = useQuery({
    queryKey: ['cm-flow-timings'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<FlowTiming[]> => {
      const res = await apiClient.get('/flows/timings', { params: { days: 90 } });
      return (res.data?.data ?? res.data ?? []) as FlowTiming[];
    },
  });
  return q.data ?? [];
}

export function ManagerVisitsPage() {
  const go = useNavigate();
  const narrow = useNarrowViewport();
  const timings = useFlowTimings();
  const [latencyPeriod, setLatencyPeriod] = useState<LatencyPeriod>(narrow ? 'month' : 'week');
  const latency = useResultsLatency(latencyPeriod);
  const [params] = useSearchParams();
  const [month, setMonth] = useState(lastCompleteMonth());
  const [compare, setCompare] = useState(shiftMonth(lastCompleteMonth(), -1));
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(params.get('doctor'));

  const range = useMemo(() => monthRange(month), [month]);
  const appointments = useAppointments(range);
  const invoices = useInvoices(range);
  const { providers } = useProviders();
  const unsigned = useUnsignedNotes();
  /* A month still being read is not a month with nothing in it. */
  const counting = appointments.isLoading || invoices.isLoading;
  const branches = useBranches();
  const branchName = branches[0]?.name ?? 'This clinic';

  usePageMeta({ title: 'Visits & doctors' });

  const booked = appointments.page.rows;
  const visits = booked.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show');
  const noShows = booked.filter((a) => a.status === 'no-show').length;
  const followUps = visits.filter((a) => a.appointmentType === 'follow-up').length;
  const newVisits = visits.length - followUps;
  const noShowRate = booked.length ? Math.round((noShows / booked.length) * 100) : 0;

  /* FOLLOW-UPS KEPT — of the follow-ups BOOKED this month, the share that were
     actually attended. Cancelled and no-show follow-ups are the ones not kept,
     which is the whole point of the figure. */
  const followUpsBooked = booked.filter((a) => a.appointmentType === 'follow-up').length;
  const followUpsKept = followUpsBooked
    ? Math.round((followUps / followUpsBooked) * 100) : null;

  /* What each visit was worth, so a doctor's average has a real numerator. */
  const paidByAppointment = useMemo(() => {
    const m = new Map<string, number>();
    for (const inv of invoices.page.rows) {
      if (!inv.appointmentId) continue;
      m.set(inv.appointmentId, (m.get(inv.appointmentId) ?? 0) + Number(inv.amountPaid || 0));
    }
    return m;
  }, [invoices.page.rows]);

  interface DoctorVisits {
    id: string;
    name: string;
    visits: number;
    followUps: number;
    booked: number;
    noShows: number;
    money: number;
    unsigned: number;
    /** Follow-ups booked with this doctor, whatever became of them. */
    fuBooked: number;
  }

  const byDoctor: DoctorVisits[] = useMemo(() => {
    const m = new Map<string, DoctorVisits>();
    for (const p of providers) {
      m.set(p.id, {
        id: p.id, name: doctorName(p), visits: 0, followUps: 0, booked: 0, noShows: 0,
        money: 0, unsigned: 0, fuBooked: 0,
      });
    }
    for (const a of booked) {
      const row = a.providerId ? m.get(a.providerId) : undefined;
      if (!row) continue;
      row.booked += 1;
      if (a.appointmentType === 'follow-up') row.fuBooked += 1;
      if (a.status === 'no-show') { row.noShows += 1; continue; }
      if (a.status === 'cancelled') continue;
      row.visits += 1;
      if (a.appointmentType === 'follow-up') row.followUps += 1;
      row.money += paidByAppointment.get(a.id) ?? 0;
    }
    for (const r of unsigned.page.rows) {
      const row = r.providerId ? m.get(r.providerId) : undefined;
      if (row) row.unsigned += 1;
    }
    return [...m.values()].filter((d) => d.booked > 0)
      .sort((a, b) => b.visits - a.visits);
  }, [providers, booked, paidByAppointment, unsigned.page.rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return byDoctor;
    return byDoctor.filter((d) => d.name.toLowerCase().includes(q));
  }, [byDoctor, query]);

  const selected = selectedId ? byDoctor.find((d) => d.id === selectedId) ?? null : null;
  const { data: run } = usePayrollRun(month);
  const qc = useQueryClient();
  /* POST /payroll/lines/:id/settle — the manager holds payout:write, and the
     server refuses it until the run is approved. */
  const settle = useMutation({
    mutationFn: async (lineId: string) => { await apiClient.post(`/payroll/lines/${lineId}/settle`); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['payroll'] }); },
  });
  const selectedLine = selected ? run?.lines.find((l) => l.personId === selected.id) ?? null : null;

  /* Busiest hours — every booked hour of the month, 8am through 10pm. */
  const hours = useMemo(() => {
    const counts = new Map<number, number>();
    for (const a of visits) {
      const h = Number((a.time || '00:00:00').slice(0, 2));
      counts.set(h, (counts.get(h) ?? 0) + 1);
    }
    const present = [...counts.keys()].sort((a, b) => a - b);
    const from = present.length ? present[0] : 9;
    const to = present.length ? present[present.length - 1] : 18;
    return Array.from({ length: Math.max(1, to - from + 1) }, (_, i) => {
      const h = from + i;
      return { key: String(h), value: counts.get(h) ?? 0, label: `${hour12(h)} — ${counts.get(h) ?? 0} visits` };
    });
  }, [visits]);

  const peakHour = hours.reduce<{ key: string; value: number } | null>(
    (p, h) => (!p || h.value > p.value ? h : p), null);

  const rows: TableRow[] = shown.map((d) => {
    const ret = d.visits ? Math.round((d.followUps / d.visits) * 100) : 0;
    const ns = d.booked ? Math.round((d.noShows / d.booked) * 100) : 0;
    return {
      id: d.id,
      cells: {
        doctor: (
          <div>
            <p className="t-strong">{d.name}</p>
            <p className="t-cap mut">{branchName} · {d.booked} booked</p>
          </div>
        ),
        visits: <span className="num">{num(d.visits)}</span>,
        avg: <span className="num">{d.visits ? num(d.money / d.visits) : '—'}</span>,
        ret: <span className={`${ret >= 40 ? 'grn' : 'amb'} w6`}>{ret}%</span>,
        noshow: ns >= 8 ? <span className="amb w6">{ns}%</span> : <>{ns}%</>,
      },
    };
  });

  const paneSig = `${selected?.id ?? ''}:${selected?.visits ?? 0}:${selected?.noShows ?? 0}:${selected?.unsigned ?? 0}:${selected?.fuBooked ?? 0}`;

  const crumb = usePhoneCrumb('Reports', `Visits · ${monthChip(month).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      /* g_c3: opened from Home's doctor row, the screen is "Home ▸ Dr. Mona R.". */
      paneTitle: selected ? selected.name : undefined,
      ...(params.get('doctor') ? { panePhoneCrumb: 'Home' } : null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Visits & doctors',
      sub: `${monthLabel(month)} · all branches · vs ${monthName(compare)}`,
      search: 'Search a doctor, an hour, a flow…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`visits-${month}`,
            ['Doctor', 'Visits', 'Avg EGP', 'Return %', 'No-show %'],
            byDoctor.map((d) => [
              d.name, d.visits, d.visits ? Math.round(d.money / d.visits) : '',
              d.visits ? Math.round((d.followUps / d.visits) * 100) : 0,
              d.booked ? Math.round((d.noShows / d.booked) * 100) : 0,
            ])),
      },
      pane: selected ? (
        <Pane
          head={narrow ? undefined : (
            <PaneHead
              overline="The doctor of the selected row"
              title={`${selected.name} — ${monthLabel(month).split(' ')[0]}`}
              sub={`${branchName} · ${selected.booked} booked`}
            />
          )}
          foot={narrow ? undefined : (
            <button type="button" className="btn btn-outline" style={{ flex: 1 }}
              onClick={() => go('/clinic-manager/reports/revenue')}>
              Their money ›
            </button>
          )}
        >
          {narrow ? (
            <>
              <Segmented
                pill="brand"
                label="Period"
                active={latencyPeriod}
                onSelect={(k) => setLatencyPeriod(k as LatencyPeriod)}
                items={[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This week' },
                  { key: 'month', label: 'This month' },
                ]}
              />
              <Card>
                <p className="t-sec sec">
                  {selected.visits} visits · {selected.visits - selected.followUps} new / {selected.followUps} follow-up
                  {selected.money ? ` · ${money(selected.money)}` : ''}
                </p>
                <p className="t-sec sec">Covers: {branchName}</p>
              </Card>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, margin: '8px 0' }}>
                <StatCard label="Unsigned" value={num(selected.unsigned)} tone={selected.unsigned ? 'warn' : undefined} />
                <StatCard
                  label="Results latency"
                  value={(() => {
                    const mine = latency.find((l) => l.doctorId === selected.id);
                    return mine && mine.avgDays !== null ? `${mine.avgDays} d` : '—';
                  })()}
                />
                <StatCard
                  label="F/U kept"
                  value={selected.fuBooked ? `${Math.round((selected.followUps / selected.fuBooked) * 100)}%` : '—'}
                />
              </div>
              <Card kind="accent">
                <p className="t-ovl mut">Compensation</p>
                <DealEditor key={selected.id} personId={selected.id} />
                {selectedLine ? (
                  <>
                    <p className="t-strong" style={{ marginTop: 8 }}>
                      {selectedLine.reverse ? 'Owes the clinic' : 'Owed this month'}: {money(Math.abs(selectedLine.netMinor) / 100)}
                      {selectedLine.dealSummary ? <span className="t-sec mut"> ({selectedLine.dealSummary})</span> : null}
                    </p>
                    {selectedLine.status === 'settled' ? (
                      <p className="t-cap grn mt8">
                        settled{selectedLine.settledAt ? ` ${new Date(selectedLine.settledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                      </p>
                    ) : selectedLine.id && !selectedLine.reverse
                      && (run?.status === 'approved' || run?.status === 'settled') ? (
                      <button type="button" className="chip chip-success w6 mt8" disabled={settle.isPending}
                        onClick={() => settle.mutate(selectedLine.id as string)}>
                        {settle.isPending ? 'Marking…' : 'Mark settled'}
                      </button>
                    ) : (
                      <p className="t-cap mut">{run?.status === 'sent' ? 'awaiting approval' : run?.status === 'draft' ? 'draft run' : run?.status}</p>
                    )}
                    {settle.isError ? <p className="t-cap amb mt4">That did not save — nothing was changed.</p> : null}
                  </>
                ) : (
                  <p className="t-sec mut" style={{ marginTop: 4 }}>No payout line this month yet.</p>
                )}
              </Card>
              <p className="t-sec mut" style={{ textAlign: 'center', marginTop: 12 }}>
                Counts, not scores — and never patient data.
              </p>
            </>
          ) : (
          <>
          <Card>
            <p className="t-ovl mut">Volume</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {selected.visits} visits ·{' '}
              {selected.visits
                ? `${Math.round(((selected.visits - selected.followUps) / selected.visits) * 100)} : ${Math.round((selected.followUps / selected.visits) * 100)}`
                : '0 : 0'}{' '}
              new/follow-up
            </p>
            <p className="t-cap mut">{branchName} · one branch</p>
          </Card>
          <Card>
            <p className="t-ovl mut">Return · no-show</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              <span className={`${selected.visits && selected.followUps / selected.visits >= 0.4 ? 'grn' : 'amb'} w6`}>
                {selected.visits ? Math.round((selected.followUps / selected.visits) * 100) : 0}%
              </span>{' '}
              return ·{' '}
              <span className={`${selected.booked && selected.noShows / selected.booked >= 0.08 ? 'amb' : 'sec'} w6`}>
                {selected.booked ? Math.round((selected.noShows / selected.booked) * 100) : 0}%
              </span>{' '}
              no-show
            </p>
            <p className="t-cap mut">
              follow-ups seen {selected.followUps} · no-shows {selected.noShows} of {selected.booked} booked
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Discipline</p>
            {/* The board's "2 unsigned · follow-ups kept 62%" — this doctor's
                own figures for the month. */}
            <p className={`t-sec ${selected.unsigned ? 'amb' : 'sec'}`} style={{ marginTop: 4 }}>
              {selected.unsigned} unsigned{selected.unsigned ? '' : ' ✓'}
              <span className="mut"> · follow-ups kept{' '}
                {selected.fuBooked
                  ? `${Math.round((selected.followUps / selected.fuBooked) * 100)}%`
                  : 'no follow-ups booked'}</span>
            </p>
          </Card>
          <Card kind="accent">
            <p className="t-sec brand">
              Amber is a conversation, not a patient list. The doctor resolves it inside their own
              account — the manager sees the count, never the notes.
            </p>
          </Card>
          </>
          )}
        </Pane>
      ) : undefined,
    },
    [narrow, latencyPeriod, latency.map((l) => l.avgDays).join(','), month, compare, query, paneSig, branchName, selectedId, selectedLine?.netMinor, selectedLine?.status, run?.status, settle.isPending, settle.isError],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={(m) => { setMonth(m); setSelectedId(null); }}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />

        <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          {/* NO SUB LINES. The board's stat cards are a label and a figure;
              a third line makes every card 18px taller and the row stops
              lining up with the table under it. */}
          <StatCard label="Visits" value={counting ? '…' : num(visits.length)} />
          <StatCard
            label="New : follow-up"
            value={counting ? '…' : visits.length
              ? `${Math.round((newVisits / visits.length) * 100)} : ${Math.round((followUps / visits.length) * 100)}`
              : '0 : 0'}
          />
          <StatCard label="No-show" value={counting ? '…' : `${noShowRate}%`} tone={noShowRate >= 8 ? 'warn' : undefined} />
          <StatCard label="Follow-ups kept" value={followUpsKept === null ? '—' : `${followUpsKept}%`} />
        </div>

        <DataTable
          caption="Visits by doctor"
          phoneLabels
          columns={[
            { key: 'doctor', label: 'Doctor', width: 46 },
            { key: 'visits', label: 'Visits', width: 13 },
            { key: 'avg', label: 'Avg EGP', width: 13 },
            { key: 'ret', label: 'Return', width: 14 },
            { key: 'noshow', label: 'No-show', width: 14 },
          ]}
          rows={rows}
          selectedId={selectedId}
          onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
          empty={counting ? <p className="t-sec mut">Counting the month…</p> :
            <EmptyState
              title="Nothing was booked this month"
              sub="A doctor appears here with their volume, their return rate and their no-shows the moment the month has a booking in it."
            />}
        />

        <div style={{ height: 8 }} />

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Busiest hours</p>
              <DayBars
                bars={hours}
                height={70}
                left={hours.length ? hour12(Number(hours[0].key)) : '—'}
                middle={peakHour && peakHour.value > 0 ? `${hour12(Number(peakHour.key))} peak` : 'no visits yet'}
                right={hours.length ? hour12(Number(hours[hours.length - 1].key)) : '—'}
              />
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {timings.length ? (
              <Card>
                <p className="t-ovl mut mb8">Flow timings · average minutes per step</p>
                {timings.slice(0, 3).map((t) => (
                  <div key={t.flow} style={{ marginBottom: 8 }}>
                    <p className="t-sec">
                      <span className="w6">{t.flow}</span> — {t.steps.map((s) => `${s.label} ${s.avgMin}`).join(' · ')}
                    </p>
                    {t.bottleneck ? <p className="t-cap amb">bottleneck: {t.bottleneck}</p> : null}
                  </div>
                ))}
                <button type="button" className="w6 brand"
                  style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
                  onClick={() => go('/clinic-manager/settings/flows')}>
                  Open the flow, step by step ›
                </button>
                <p className="t-cap mut mt8">
                  Every step of a flow records its own duration, so the report can say where the
                  clinic’s minutes actually go.
                </p>
              </Card>
            ) : null}
          </div>
        </div>

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Per-doctor return and no-show, aggregate only. No visit lists, no names, no diagnoses —
          the wall holds inside the reports too.
        </p>
      </div>
    </div>
  );
}

function hour12(h: number): string {
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour} ${suffix}`;
}
